import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Box } from '@mui/system';
import Stack from '@mui/material/Stack';
import {
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  TextField,
} from '@mui/material';

import useClasses from '../../../hooks/legacy/useClasses';
import CheckoutLayoutItem from '../CheckoutLayoutItem';
import useTotalCartPrice from '../../../util/hooks/UseCart';
import StripeCheckoutComponent from '../StripeCheckoutComponent';

import visaCard from '../../../assets/images/visaCard.png';
import tickIcon from '../../../assets/images/icons/tickIcon.png';
import visaCardIcon from '../../../assets/images/visaCardIcon.png';

import styles from './OrderPayment.style';

function OrderPayment({
  onCompleteCheckout,
  onAdjustedValue,
  onCheckoutUpdating,
  redeemGCCode,
}) {
  const classes = useClasses(styles);

  const { subtotalPrice } = useTotalCartPrice();

  const [payment, setPayment] = useState(null);
  const [payments, setPayments] = useState([]);
  const [gcCodeString, setGCCodeString] = useState('');

  const {
    checkoutData,
    minimums,
    orderType,
    updatingCartItems,
    loaderQueue,
    cartItems,
    isMinimumOrder,
    availableData: gcAvailableData,
    redeemData: gcRedeemData,
  } = useSelector(
    ({
      main,
      order,
      restaurant,
      address: selectedAddress,
      time: selectedTime,
      gift,
    }) => ({
      checkoutData: order.checkoutData,
      minimums: order.minimums,
      orderType: order?.type,
      updatingCartItems: order.updatingCartItems,
      loaderQueue: main.globalLoaderQueue,
      cartItems: order.cartItems,
      time: selectedTime.selectedTime,
      address: selectedAddress.selectedAddress,
      selectedRestaurant: restaurant.selectedRestaurantId,
      orderPhoneNumber: order.orderPhoneNumber,
      isMinimumOrder: order.isMinimumOrder,
      availableData: gift.availableData,
      redeemData: gift.redeemData,
    })
  );

  const minOrderPrice = useMemo(
    () => minimums?.[orderType.value] || 0,
    [minimums, orderType]
  );

  const isMinOrderPrice = useMemo(
    () => minOrderPrice <= subtotalPrice,
    [minOrderPrice, subtotalPrice]
  );

  const isPurchaseEnabled =
    !updatingCartItems.length > 0 &&
    isMinOrderPrice &&
    !loaderQueue.length > 0 &&
    cartItems.length > 0 &&
    !onCheckoutUpdating;

  const onClick = () => {
    onCompleteCheckout();
  };

  const handleChangeGCCode = (value) => {
    setGCCodeString(value);
  };

  const handleRedeem = () => {
    redeemGCCode(gcCodeString);
    setGCCodeString('');
  };

  useEffect(() => {
    if (!payment) {
      setPayment(checkoutData?.payments[0].type);

      const listPayments = Object.prototype.hasOwnProperty.call(
        checkoutData || {},
        'payments'
      )
        ? [checkoutData.payments[0]]
        : [];
      setPayments(listPayments);
    }
  }, [checkoutData, payment]);

  return (
    <CheckoutLayoutItem title="Payment">
      <Stack
        spacing={3}
        direction="column"
        alignItems="flex-end"
        justifyContent="center"
      >
        {payments.map(({ type }) => {
          if (type === 'cash') return '';

          return (
            <Box
              key={`payment-type-item-${type}`}
              className={classes.parentBoxBorder}
            >
              <Box className={classes.parentBoxDisplay}>
                <Box ml={3}>
                  <img src={tickIcon} alt="no icon" />
                </Box>
                <Box ml={1}>
                  <img src={visaCardIcon} alt="no icon" />
                </Box>
                <Box className={classes.visaCardMargin}>
                  <img src={visaCard} alt="no icon" />
                </Box>
                <Box className={classes.cardTextColor}>
                  Pay by Credit/Debit Card
                </Box>
              </Box>
            </Box>
          );
        })}
        {gcAvailableData.data?.available_balance > 0 && (
          <Box className={classes.balanceBox}>
            <Box data-test-id="gc-balance">Your Available Balance</Box>
            <Box className={classes.balanceCheckBoxBorder}>
              <FormControlLabel
                data-test-id="gc-balance-checkbox"
                control={
                  <Checkbox
                    className={classes.balanceCheckbox}
                    onChange={(e) => {
                      if (e.target.checked) {
                        onAdjustedValue('gift_certificate', {
                          amount: gcAvailableData.data?.available_balance,
                          use_full_amount: true,
                        });
                      } else {
                        onAdjustedValue('gift_certificate', null);
                      }
                    }}
                    size="small"
                    data-test-id="gc-balance-check"
                  />
                }
                label={`Apply ${gcAvailableData.data.available_balance} gift card balance`}
              />
            </Box>
          </Box>
        )}
        <Box className={classes.giftCode}>
          <Box className={classes.redeemColor}>Add Gift Card to Balance</Box>
          <Box className={classes.gcCodeInputWidth}>
            <TextField
              InputProps={{
                className: classes.gcCodeField,
                'data-test-id': 'gc-code-input',
              }}
              variant="outlined"
              value={gcCodeString}
              onChange={(e) => handleChangeGCCode(e?.target?.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleRedeem()}
              fullWidth
              placeholder="Enter gift card code"
              data-test-id="gc-add-balance"
              disabled={onCheckoutUpdating}
            />

            <Button
              className={classes.giftCodeSubmitBtn}
              variant="contained"
              data-test-id="giftcode-submit"
              onClick={handleRedeem}
              disabled={onCheckoutUpdating || gcRedeemData.loading}
            >
              Redeem
            </Button>
          </Box>
        </Box>
        <Grid item lg={10} xs={12} className={classes.placeOrderButton}>
          {payment === 'stripe' ? (
            <StripeCheckoutComponent
              orderType={orderType.label}
              data-test-id="place-order-stripe"
              isPurchaseEnabled={isPurchaseEnabled}
              amount={(checkoutData?.total || 0) * 100}
              onClick={() => isPurchaseEnabled && onClick()}
            />
          ) : (
            <Button
              size="large"
              variant="contained"
              data-test-id="place-order-cash"
              onClick={() => isPurchaseEnabled && onClick()}
              disabled={
                !isPurchaseEnabled || isMinimumOrder || onCheckoutUpdating
              }
              sx={{ fontSize: 16, height: 64, width: '100%' }}
            >
              Place Order
            </Button>
          )}
        </Grid>
      </Stack>
    </CheckoutLayoutItem>
  );
}

OrderPayment.propTypes = {
  onCompleteCheckout: PropTypes.func.isRequired,
  onAdjustedValue: PropTypes.func.isRequired,
  onCheckoutUpdating: PropTypes.bool.isRequired,
  redeemGCCode: PropTypes.func.isRequired,
};

export default OrderPayment;
